import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { DisconnectedPlug, Search } from '@lyearn/icons';
import { EmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import VirtualizedGrid from '@/components/VirtualizedGrid';
import { usePaginatedUnsplash } from '@/hooks/useUnsplash';
import useImageList from '@/modules/corporate/components/ImageList/hooks/useImageList';

import ImageCard from './ImageCard';
import ImageUploadCard from './ImageUploadCard';
import Loader from './Loader';
import { ImageListProps } from './types';

const ImageList: React.FC<ImageListProps> = (props) => {
  const {
    coverUrl,
    selectedCard,
    setCard,
    items,
    searchString,
    uppy,
    showRandom = true,
    ListLoader = Loader,
    overrides = {},
    staticImageType,
  } = props;

  const { t } = useTranslation();

  const {
    className = 'overflow-y-auto flex-grow w-full -mt-20',
    listClassName = 'px-12 mt-32 grid-cols-2',
  } = overrides;

  const { initialFetching, error, data, fetchMore } = usePaginatedUnsplash({
    query: searchString === '' ? 'random' : searchString,
    perPage: 15,
  });
  const { cardItems, onClickCard } = useImageList({
    data,
    searchString,
    items,
    selectedCard,
    showRandom,
    setCard,
    staticImageType,
    showUploadCard: Boolean(uppy),
  });

  const renderCard = (index: number) => {
    if (uppy && index === 0 && searchString === '') {
      return <ImageUploadCard uppy={uppy} />;
    }

    if (!cardItems[index]) {
      return null; // safari old version issue
    }

    return (
      <ImageCard
        cardItem={cardItems[index]}
        coverUrl={coverUrl}
        selectedCard={selectedCard}
        onClick={onClickCard}
      />
    );
  };

  if (initialFetching) {
    return <ListLoader />;
  }

  if (error) {
    return (
      <Box className={className}>
        <EmptyState
          Icon={DisconnectedPlug}
          className="h-4/5"
          description={t('Common:Error.SomethingWentWrongDescription')}
          title={t('Common:Error.SomethingWentWrong')}
        />
      </Box>
    );
  }

  if (cardItems.length === 0) {
    return (
      <Box className={className}>
        <EmptyState
          Icon={Search}
          className="h-4/5"
          description={t('Common:Error.SearchNoResultsDescription')}
          title={t('Common:NoResultsFound')}
        />
      </Box>
    );
  }

  return (
    <Box className={className}>
      <VirtualizedGrid
        endReached={fetchMore}
        itemContent={renderCard}
        listClassName={cn('grid grid-flow-row gap-y-4 gap-x-4 items-stretch', listClassName)}
        totalCount={cardItems.length}
      />
    </Box>
  );
};

export default ImageList;
